import { Autocomplete, Button, Link, OutlinedInput, Stack, Typography } from "@mui/material";
import { GridColDef, GridRowsProp } from "@mui/x-data-grid";
import React from "react";
import Modal, { CommonModalProps } from "..";
import DataTable, { CellSize } from "../../Table/table";
import TableCellMultipleSelect from "../../Table/TableCellMultipleSelect/table-cell-multiple-select";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { AddItemToBasketRequest } from "../../../Core/Api/Types/add-item-to-basket-request";
import { produce } from "immer";
import { ContractAndRightType } from "../../../Core/Api/Right/right-api-service";
import { useTranslation } from "react-i18next";

type CloseClbProps = {
    confirm: boolean;
};

export type ModalAddSeasonToBasketForRightsElementProps = {
    onClose?: (props: CloseClbProps) => void;
    onConfirm?: (request: AddItemToBasketRequest[]) => void;
    data: ContractAndRightType[];
    request: AddItemToBasketRequest[];
    context: "Simple" | "Multiple";
};

export type ModalAddSeasonToBasketForRightsProps = CommonModalProps<ModalAddSeasonToBasketForRightsElementProps>;

const ModalAddSeasonToBasketForRights = React.forwardRef<any, ModalAddSeasonToBasketForRightsProps>((props, ref) => {
    const { closeModal, onClose, onConfirm, data, request, context, ...modalProps } = props;
    const { t } = useTranslation();
    const reference = React.useRef(undefined);
    const [updatedRequest, setUpdatedRequest] = React.useState<AddItemToBasketRequest[]>(request);

    const [season, setSeason] = React.useState<ContractAndRightType>();
    const [seasonEpisodes, setSeasonEpisodes] = React.useState<number[]>([]);
    const [seasonEpisodesOptions, setSeasonEpisodesOptions] = React.useState<number[]>([]);

    // Select/Unselect All Simple
    const handleSelectAllClick = () => {
        handleEditEpisodes(seasonEpisodesOptions);
    };
    const handleUnselectAllClick = () => {
        handleEditEpisodes([]);
    };

    // Select/Unselect All Multiple
    const handleUnselectAllClickMulti = (externalId: string, id: number) => {
        // here update with
        setUpdatedRequest(
            produce((draft) => {
                let item = draft.find((a) => a.objectId === id && a.externalContractId === externalId);
                item!.episodesNbList = [];
            })
        );
    };

    const handleSelectAllClickMulti = (externalId, id) => {
        setUpdatedRequest(
            produce((draft) => {
                let item = draft.find((a) => a.objectId === id && a.externalContractId === externalId);
                item!.episodesNbList = Array.from(
                    {
                        length: data.find((x) => x.externalId === externalId)!.titleNbEpisodes ?? 0,
                    },
                    (item, index) => (item = index + 1)
                );
            })
        );
    };

    React.useEffect(() => {
        if (context === "Simple") {
            let mySeason = data.find((x) => x.titleType === "Season");
            setSeason(mySeason);
            setSeasonEpisodes(Array.from({ length: mySeason!.titleNbEpisodes ?? 0 }, (item, index) => (item = index + 1)));
            setSeasonEpisodesOptions(
                Array.from({ length: mySeason!.titleNbEpisodes ?? 0 }, (item, index) => (item = index + 1))
            );
        }
    }, []);

    // Table creation
    const rows: GridRowsProp = data;
    type Row = (typeof rows)[number];
    const columns: GridColDef[] = [
        {
            field: "titleInfos",
            headerName: t("common:name"),
            flex: CellSize.medium,
            minWidth: CellSize.medium,
            renderCell: (data: Row) => (
                <Typography variant="body2" color="text.primary" title={data.value.name}>
                    {data.value.name}
                </Typography>
            ),
        },
        {
            field: "customerName",
            headerName: t("common:customer"),
            flex: CellSize.medium,
            minWidth: CellSize.medium,
            renderCell: (data: Row) => (
                <Typography variant="body2" color="text.primary" title={data.value}>
                    {data.value}
                </Typography>
            ),
        },
        {
            field: "contractId",
            headerName: t("table:headerName.contractId"),
            flex: CellSize.xxsmall,
            minWidth: CellSize.xxsmall,
            renderCell: (data: Row) => (
                <Typography variant="body2" color="text.primary" title={data.value}>
                    {data.value}
                </Typography>
            ),
        },
        {
            field: "",
            headerName: t("common:episodes"),
            flex: CellSize.xxlarge,
            minWidth: CellSize.xxlarge,
            renderCell: (data: Row) => {
                return (
                    <TableCellMultipleSelect
                        request={
                            new AddItemToBasketRequest({
                                objectId: data?.row?.titleId,
                                episodesNbList: [],
                                externalContractId: data?.row?.externalId,
                            })
                        }
                        values={updatedRequest.find((x) => x.externalContractId === data?.row?.externalId)?.episodesNbList || []}
                        options={Array.from({ length: data?.row?.titleNbEpisodes }, (item, index) => (item = index + 1))}
                        onChange={handleUpdateEpisodes}
                    />
                );
            },
        },
        {
            field: "selectAll",
            headerName: "",
            minWidth: CellSize.small,
            flex: CellSize.small,
            renderCell: (data: Row) => {
                return (
                    <Link
                        underline="hover"
                        variant="body2"
                        color="primary.text"
                        onClick={() => handleSelectAllClickMulti(data?.row?.externalId, data?.row?.titleId)}
                    >
                        {t("common:selectAllUpperCase")}
                    </Link>
                );
            },
        },
        {
            field: "unselectAll",
            headerName: "",
            minWidth: CellSize.small,
            flex: CellSize.small,
            renderCell: (data: Row) => {
                return (
                    <Link
                        underline="hover"
                        variant="body2"
                        color="primary.text"
                        onClick={() => handleUnselectAllClickMulti(data?.row?.externalId, data?.row?.titleId)}
                    >
                        {t("common:unselectAllUpperCase")}
                    </Link>
                );
            },
        },
    ];

    const handleCancelClick = () => {
        onClose && onClose({ confirm: false });
        closeModal();
    };

    const handleConfirmClick = () => {
        onConfirm && onConfirm(updatedRequest);
        closeModal();
    };

    const handleUpdateEpisodes = (episodes: number[], externalId: string, id: number) => {
        // onChange && onChange(episodes, externalId, id);
        setUpdatedRequest(
            produce((draft) => {
                let item = draft.find((a) => a.externalContractId === externalId);
                item!.episodesNbList = episodes;
            })
        );
    };

    //this for simple
    const handleEditEpisodes = (value) => {
        setSeasonEpisodes(value);
        // onChange && onChange(value, season?.externalId || "", season?.titleId || 0);
        setUpdatedRequest(
            produce((draft) => {
                let item = draft.find((a) => a.externalContractId === season?.externalId);
                item!.episodesNbList = value;
            })
        );
    };

    return (
        <Modal
            {...modalProps}
            title={t("modal:title.addEpisodesToBasket")}
            reference={reference}
            closeModal={handleCancelClick}
            width={800}
            footer={
                <Stack direction="row" justifyContent="space-between">
                    <Button fullWidth variant="contained" color="primary" onClick={handleConfirmClick}>
                        {t("common:addToBasket")}
                    </Button>
                </Stack>
            }
        >
            {context === "Multiple" && (
                <DataTable
                    noControls
                    rows={data.filter((x) => x.titleType === "Season")}
                    columns={columns}
                    rowHeight={80}
                    disableRowSelectionOnClick
                    getRowId={(row) => row.id}
                />
            )}

            {context === "Simple" && season && (
                <>
                    <Typography mb={1} component="span" color="text.primary">
                        {t("modal:body.selectEpisodesToAddToBasket")}
                    </Typography>
                    <Autocomplete
                        ref={reference}
                        fullWidth
                        options={Array.from({ length: season.titleNbEpisodes ?? 0 }, (item, index) => (item = index + 1))}
                        value={seasonEpisodes}
                        onChange={(e, value) => handleEditEpisodes(value)}
                        popupIcon={<ExpandMoreRoundedIcon />}
                        multiple
                        getOptionLabel={(option) => String(option)}
                        renderInput={(params) => (
                            <OutlinedInput {...params.InputProps} inputProps={params.inputProps} required sx={{ width: "100%" }} />
                        )}
                    />
                    <Stack mt={2} direction="row" justifyContent="flex-end" spacing={1}>
                        <Link underline="hover" variant="body2" color="primary.text" onClick={handleSelectAllClick}>
                            {t("common:selectAllUpperCase")}
                        </Link>
                        <Link underline="hover" variant="body2" color="primary.text" onClick={handleUnselectAllClick}>
                            {t("common:unselectAllUpperCase")}
                        </Link>
                    </Stack>
                </>
            )}
        </Modal>
    );
});

export default ModalAddSeasonToBasketForRights;
